// Libraries
import React from 'react';

// Supermove
import {Icon, IconSource, PreventPropagation, Space, Styled} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {usePopover, useResponsive} from '@supermove/hooks';
import {ProjectModel} from '@supermove/models';
import {Phone} from '@supermove/utils';

// Shared
import IconButton from '@shared/design/components/IconButton';
import ProjectSendEmailOptionsPopover from '@shared/modules/Project/components/ProjectSendEmailOptionsPopover';

const PhoneNumberButton = Styled.ButtonV2`
`;

const ActionButton = ({
  icon,
  onPress,
  isDisabled,
  isDisplayOnly,
  tooltip,
  isActive = false,
}: {
  icon: IconSource;
  onPress?: () => void;
  isDisplayOnly?: boolean;
  isDisabled: boolean;
  tooltip: string;
  isActive?: boolean;
}) => {
  const responsive = useResponsive();
  return (
    <IconButton
      source={icon}
      isSecondary
      isDisabled={isDisabled}
      onPress={onPress}
      isDisplayOnly={isDisplayOnly}
      isHoverDistinct
      tooltip={tooltip}
      tooltipPlacement={'top'}
      isEnabledMobileToastTooltip
      isActive={isActive}
      isHitSlop={!responsive.desktop}
    />
  );
};

const CallButton = ({project, handleCall}: {project: ProjectModel; handleCall?: () => void}) => {
  const {phoneNumber} = project.client.primaryContact;
  const icon = Icon.PhoneAlt;
  const tooltip = phoneNumber
    ? `Call ${Phone.display(phoneNumber)}`
    : 'No phone number available for this contact.';
  const isDisabled = !phoneNumber;

  if (handleCall) {
    return (
      <ActionButton icon={icon} isDisabled={isDisabled} tooltip={tooltip} onPress={handleCall} />
    );
  }
  return (
    <PhoneNumberButton>
      <a href={phoneNumber ? Phone.createUrl(phoneNumber) : undefined}>
        <ActionButton icon={icon} isDisabled={isDisabled} tooltip={tooltip} isDisplayOnly />
      </a>
    </PhoneNumberButton>
  );
};

const SmsButton = ({project, handleSms}: {project: ProjectModel; handleSms: () => void}) => {
  const {phoneNumber} = project.client.primaryContact;

  return (
    <ActionButton
      icon={Icon.CommentSms}
      onPress={handleSms}
      isDisabled={!phoneNumber}
      tooltip={
        phoneNumber
          ? `Text ${Phone.display(phoneNumber)}`
          : 'No phone number available for this contact.'
      }
    />
  );
};

const EmailButton = ({
  project,
  handleEmail,
}: {
  project: ProjectModel;
  handleEmail: (emailTemplateUuid?: string) => void;
}) => {
  const {email} = project.client.primaryContact;
  const projectSendEmailOptionsPopover = usePopover();

  return (
    <ProjectSendEmailOptionsPopover
      actionMenuPopover={projectSendEmailOptionsPopover}
      handleComposeEmail={handleEmail}
    >
      <PreventPropagation>
        <ActionButton
          icon={Icon.Envelope}
          onPress={projectSendEmailOptionsPopover.handleToggle}
          isDisabled={!email}
          tooltip={email ? `Email ${email}` : 'No email available for this contact.'}
          isActive={projectSendEmailOptionsPopover.isOpen}
        />
      </PreventPropagation>
    </ProjectSendEmailOptionsPopover>
  );
};

const ProjectContactActions = ({
  project,
  handleCall,
  handleSms,
  handleEmail,
  isOfficeAppEnabled,
}: {
  project: ProjectModel;
  handleCall?: () => void;
  handleSms: () => void;
  handleEmail: (emailTemplateUuid?: string) => void;
  isOfficeAppEnabled: boolean;
}) => {
  const responsive = useResponsive();
  return (
    <React.Fragment>
      {isOfficeAppEnabled && (
        <React.Fragment>
          <EmailButton project={project} handleEmail={handleEmail} />
          <Space width={responsive.desktop ? 4 : 12} />
        </React.Fragment>
      )}
      <SmsButton project={project} handleSms={handleSms} />
      <Space width={responsive.desktop ? 4 : 12} />
      <CallButton project={project} handleCall={handleCall} />
    </React.Fragment>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
ProjectContactActions.fragment = gql`
  fragment ProjectContactActions on Project {
    id
    client {
      id
      primaryContact {
        id
        phoneNumber
        email
      }
    }
  }
`;

export default ProjectContactActions;
